import { UserService } from '../services/user';
import { defineStore } from 'pinia';
import { useAuthStore } from './auth';

export interface UserState {
    profile: API.UserProfile | null;
    subscription: SubscriptionState | null;
    billingInfo: API.UserBillingInfo | null;
}

export interface SubscriptionState extends API.UserSubscription {
    isTrial?: boolean | null;
    daysRemaining?: number;
    currentPeriodEndDate?: Date;
    endFormatted?: string;
    totalUnitAmount?: number;
}

export const useUserStore = defineStore('user', {
    state: (): UserState => {
        return {
            profile: null,
            subscription: null,
            billingInfo: null
        }
    },
    getters: {
        phone: (state) => {
            return state.profile?.phone
        }
    },
    actions: {
        async updateProfile(updateProfileReq: API.UpdateProfileReq) {
            try {
                const auth = useAuthStore();
                if (!auth.accessToken) {
                    return;
                }
                const profile = await UserService.updateProfile(auth.accessToken, updateProfileReq);
                this.setProfile(profile);
            } catch (err) {
                console.error(err);
                throw err;
            }
        },
        async updateCard(updateCardReq: API.UpdateCardReq) {
            try {
                const auth = useAuthStore();
                if (!auth.accessToken) {
                    return;
                }
                const billingInfo = await UserService.updateCard(auth.accessToken, updateCardReq);
                this.setBillingInfo(billingInfo);
            } catch (err) {
                console.error(err);
                throw err;
            }
        },
        async cancelSubscription() {
            try {
                const auth = useAuthStore();
                if (!auth.accessToken) {
                    return;
                }
                const subscription = await UserService.cancelSubscription(auth.accessToken);
                this.setSubscription(subscription);
            } catch (err) {
                console.error(err);
                throw err;
            }
        },
        async reactivateSubscription() {
            try {
                const auth = useAuthStore();
                if (!auth.accessToken) {
                    return;
                }
                const subscription = await UserService.reactivateSubscription(auth.accessToken);
                this.setSubscription(subscription);
            } catch (err) {
                console.error(err);
                throw err;
            }
        },
        async activateSubscription(activateSubscriptionReq: API.ActivateSubscriptionReq) {
            try {
                const auth = useAuthStore();
                if (!auth.accessToken) {
                    return;
                }
                const subscription = await UserService.activateSubscription(auth.accessToken, activateSubscriptionReq);
                this.setSubscription(subscription);
            } catch (err) {
                console.error(err);
                throw err;
            }
        },

        //registration actions
        async register(signupData: API.RegisterReq) { //todo check usage
            return await UserService.register(signupData);
        },
        async resendActivation(resendActivationData: API.ResendActivationReq) {//todo check usage
            return await UserService.resendActivation(resendActivationData);
        },
        async setPhone(phoneData: API.PhoneSetupReq) {
            try {
                const auth = useAuthStore();
                if (!auth.accessToken) {
                    return;
                }
                const userData = await UserService.setPhone(auth.accessToken, phoneData);
                this.setProfile(userData);
            } catch (err) {
                console.error(err);
                throw err;
            }
        },
        async editPhone() {
            try {
                const auth = useAuthStore();
                if (!auth.accessToken) {
                    return;
                }
                const userData = await UserService.editPhone(auth.accessToken);
                this.setProfile(userData);
            } catch (err) {
                console.error(err);
                throw err;
            }
        },
        async phoneCode(codeData: API.PhoneCodeReq) {
            try {
                const auth = useAuthStore();
                if (!auth.accessToken) {
                    return;
                }
                const userData = await UserService.phoneCode(auth.accessToken, codeData);
                this.setProfile(userData);
            } catch (err) {
                console.error(err);
                throw err;
            }
        },
        async resendSMS() {
            try {
                const auth = useAuthStore();
                if (!auth.accessToken) {
                    return;
                }
                await UserService.resendSMS(auth.accessToken);
            } catch (err) {
                console.error(err);
                throw err;
            }
        },
        async agreement(agreement: API.AgreementReq) {
            try {
                const auth = useAuthStore();
                if (!auth.accessToken) {
                    return;
                }
                const agreementData = await UserService.agreement(auth.accessToken, agreement);
                this.setProfile(agreementData.profile);
                this.setSubscription(agreementData.subscription);
            } catch (err) {
                console.error(err);
                throw err;
            }
        },

        //setters
        setProfile(profile: API.UserProfile | null) {
            this.profile = profile;
        },
        setSubscription(subscription: API.UserSubscription | null) {
            this.subscription = prepareSuscription(subscription);
        },
        setBillingInfo(billingInfo: API.UserBillingInfo | null) {
            this.billingInfo = billingInfo;
        },
        setAll(user: API.UserData) {
            this.profile = user.profile;
            this.subscription = prepareSuscription(user.subscription);
            this.billingInfo = user.billingInfo;
        },
        clearAll() {
            this.profile = null;
            this.subscription = null;
            this.billingInfo = null;
        }

    }
});

function prepareSuscription(sub: API.UserSubscription | null): SubscriptionState | null {
    if (sub == null) {
        return null;
    }

    const prepared: SubscriptionState = {
        isTrial: sub.plan.code?.startsWith("trial"),
        ...sub
    };

    if (sub.currentPeriodEndsAt) {
        prepared.currentPeriodEndDate = new Date(sub.currentPeriodEndsAt);
        const parts = endDateFormat.formatToParts(prepared.currentPeriodEndDate);
        prepared.endFormatted = `${parts.find(p => p.type === "month")?.value} ${parts.find(p => p.type === "day")?.value}, ${parts.find(p => p.type === "year")?.value}`;
    }

    if (prepared.unitAmount) {
        prepared.totalUnitAmount = prepared.unitAmount;
    }

    if (sub.state != "expired" && prepared.currentPeriodEndDate) {
        prepared.daysRemaining = Math.ceil((prepared.currentPeriodEndDate.getTime() - (new Date()).getTime()) / (1000 * 60 * 60 * 24));
    }

    return prepared;
}

const endDateFormat = new Intl.DateTimeFormat('en-us', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
});